import * as React from 'react'
import styled from 'styled-components'
import classnames from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import isFunction from 'lodash/isFunction'
import { Modal, Dimmer, Loader } from 'semantic-ui-react'
import { useUnmount } from 'react-nb-hooks'
import { loadFont } from 'loankeeper-common/helpers/styles'
import Window from 'loankeeper-common/components/Window'

import KeyHandler from '../KeyHandler'

import { LKModalProvider } from './LKModalContext'
import LKConfirmationModal, { openConfirmation } from './LKConfirmationModal'
import { isOpen } from './selectors'
import { open as openModal, close as closeModal } from './actions'

const MODAL_CLOSE_CONFIRM = 'MODAL_CLOSE_CONFIRM'

const LKModalWindow = styled(Window)`
  &.lk-window {
    .lk-window-header {
      background-color: var(--color-modal-background-header);

      *:not(i) {
        ${(props) => loadFont(props.theme.lkModalHeaderFont)};
      }
    }

    .lk-window__body {
      position: relative;
      padding: 10px;
      min-height: 100px;
      overflow-y: auto;
    }
  }

  &.lk-modal {
    max-width: initial;
    max-height: calc(100vh - 52px);
    ${(props) => loadFont(props.theme.lkModalContentFont)};

    &.lk-modal--xxs {
      width: 550px;
      height: 200px;
    }

    &.lk-modal--xs {
      width: 550px;
      height: 305px;
    }

    &.lk-modal--sm-1 {
      width: 550px;
      height: ${(props) => (props.autoHeight ? 'auto' : '520px')};
    }

    &.lk-modal--sm-2 {
      width: 550px;
      height: 697px;
    }

    &.lk-modal--sm-3 {
      width: 800px;
      height: 580px;
    }

    &.lk-modal--medium {
      width: 1100px;
      height: 580px;
    }

    &.lk-modal--lg {
      width: 1000px;
      height: 750px;
    }

    &.lk-modal--ml {
      width: 1200px;
      height: 750px;
    }

    &.lk-modal--xl {
      width: 1350px;
      height: 750px;
    }

    &.lk-modal--full-body .lk-window__body {
      padding: 0;
    }

    &.lk-modal--no-overflow,
    &.lk-modal--no-overflow .lk-window__body {
      overflow: initial;
    }

    *:not(i) {
      ${(props) => loadFont(props.theme.lkModalContentFont)};
    }

    .lk-window-footer__info {
      background-color: #2185d0;
      background-color: var(--color-modal-background-footer);
    }

    .lk-form-field,
    .lk-form-field *:not(i) {
      ${(props) => loadFont(props.theme.lkModalInputFont)};
    }
  }
`

// type Props = {
//   name: string,
//   onOpen?: Function,
//   dimmer?: any,
//   trigger: React.ReactNode,
//   dataAttributes: Object,
//   size: AllowedSizes,
//   icon?: string,
//   heading: string,
//   hint: string,
//   footer?: React.ReactNode,
//   loading?: boolean,
//   children: React.ReactNode,
//   closeOnEsc?: boolean,
//   confirmClose?: boolean,
//   closeBlocked?: boolean,
//   style?: Object,
//   className?: String,
//   fullBody?: Boolean,
//   noOverflow?: Boolean,
//   onClose?: Function,
// }

const LKModal = React.forwardRef(
  (
    {
      name,
      onOpen,
      dimmer,
      trigger,
      dataAttributes,
      size,
      icon,
      heading,
      hint,
      footer,
      loading,
      children,
      closeOnEsc,
      closeBlocked,
      confirmClose,
      onClose,
      fullBody,
      noOverflow,
      autoHeight = false,
      ...rest
    },
    ref,
  ) => {
    const dispatch = useDispatch()

    const open = useSelector((state) => isOpen(state, name))

    const handleOpen = React.useCallback(() => {
      if (closeBlocked) {
        return
      }

      if (isFunction(onOpen)) {
        onOpen()
      }

      dispatch(openModal({ name }))
    }, [dispatch, onOpen, name, closeBlocked])

    const handleClose = React.useCallback(() => {
      dispatch(closeModal(name))

      if (isFunction(onClose)) {
        onClose()
      }
    }, [dispatch, name, onClose])

    const handleSafeClose = React.useCallback(() => {
      if (!open) {
        return
      }

      if (confirmClose) {
        dispatch(openConfirmation(MODAL_CLOSE_CONFIRM))
      } else {
        handleClose()
      }
    }, [dispatch, open, handleClose, confirmClose])

    const context = React.useMemo(
      () => ({
        name,
        onClose: handleSafeClose,
        closeBlocked,
      }),
      [name, handleSafeClose, closeBlocked],
    )

    useUnmount(handleClose)

    return (
      <Modal
        ref={ref}
        {...rest}
        dimmer={dimmer}
        trigger={trigger}
        onOpen={handleOpen}
        onClose={handleSafeClose}
        {...dataAttributes}
        closeOnDimmerClick={false}
        closeOnEscape={false}
        closeIcon
        open
      >
        <LKModalProvider value={context}>
          <LKModalWindow
            className={classnames('lk-modal', `lk-modal--${size}`, {
              'lk-modal--full-body': fullBody,
              'lk-modal--no-overflow': noOverflow,
            })}
            icon={icon}
            headerText={heading}
            hint={hint}
            footer={footer}
            size="large"
            onClose={handleSafeClose}
            autoHeight={autoHeight}
          >
            {loading ? (
              <Dimmer active inverted>
                <Loader />
              </Dimmer>
            ) : (
              children
            )}
          </LKModalWindow>
          {closeOnEsc && <KeyHandler targetKey="27" onKey={handleSafeClose} />}
          {confirmClose && <LKConfirmationModal name={MODAL_CLOSE_CONFIRM} onConfirm={handleClose} />}
        </LKModalProvider>
      </Modal>
    )
  },
)

LKModal.defaultProps = {
  onOpen: undefined,
  dimmer: undefined,
  icon: 'lk',
  footer: null,
  loading: false,
  closeOnEsc: true,
  confirmClose: false,
  closeBlocked: false,
  style: undefined,
  className: undefined,
  fullBody: false,
  noOverflow: false,
}

export default LKModal
